import axios from 'axios';
import { API_BASE_URL } from '../config/config';

const TRAINING_VIDEO_BASE_URL = `${API_BASE_URL}/CustomerTrainingVideo`;

export interface TrainingVideo {
  uid: string;
  uniqueCode: string;
  title: string;
  description: string;
  category: string;
  filePath: string;
  fileName: string;
  fileSize: number;
  fileSizeMB: number;
  fileExtension: string;
  duration: number | null;
  thumbnailPath: string | null;
  displayOrder: number;
  isActive: boolean;
  isPublished: boolean;
  viewCount: number;
  createdTime: string;
}

export interface CategoryInfo {
  Category: string;
  VideoCount: number;
}

export interface TrainingVideoResponse {
  success: boolean;
  count?: number;
  data?: TrainingVideo[];
  message?: string;
}

export interface CategoriesResponse {
  success: boolean;
  data?: CategoryInfo[];
  message?: string;
}

class TrainingVideoService {
  /**
   * Get all training videos (optionally filtered by category)
   */
  async getVideos(category?: string, onlyPublished: boolean = true): Promise<TrainingVideoResponse> {
    try {
      const params: any = { onlyPublished };
      if (category) {
        params.category = category;
      }

      const response = await axios.get(TRAINING_VIDEO_BASE_URL, { params });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching training videos:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch training videos'
      };
    }
  }

  /**
   * Get all categories with video counts
   */
  async getCategories(): Promise<CategoriesResponse> {
    try {
      const response = await axios.get(`${TRAINING_VIDEO_BASE_URL}/categories`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch categories'
      };
    }
  }

  /**
   * Get a single video by UID
   */
  async getVideoByUid(uid: string): Promise<{ success: boolean; data?: TrainingVideo; message?: string }> {
    try {
      const response = await axios.get(`${TRAINING_VIDEO_BASE_URL}/${uid}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching video:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch video'
      };
    }
  }

  /**
   * Increment view count for a video
   */
  async incrementViewCount(uid: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axios.post(`${TRAINING_VIDEO_BASE_URL}/${uid}/view`);
      return response.data;
    } catch (error: any) {
      console.error('Error incrementing view count:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to increment view count'
      };
    }
  }

  /**
   * Get video stream URL
   */
  getVideoStreamUrl(uid: string): string {
    return `${TRAINING_VIDEO_BASE_URL}/video/${uid}`;
  }

  /**
   * Get full file path URL
   */
  getVideoFileUrl(filePath: string): string {
    // Remove leading slash if present
    const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    
    // URL-encode the path to handle spaces and special characters
    // Split by '/' to encode each segment separately
    const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/');
    
    // Construct full URL: http://192.168.0.208:5156/{filePath}
    const baseUrl = API_BASE_URL.replace('/api', '');
    return `${baseUrl}/${encodedPath}`;
  }

  /**
   * Format duration from seconds to MM:SS or HH:MM:SS
   */
  formatDuration(seconds: number | null): string {
    if (!seconds) return '--:--';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
}

export default new TrainingVideoService();

